import { useState, useEffect, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, Plus, Pencil, Trash2, Loader2, GripVertical, Upload, ImageOff } from "lucide-react";

const DISCIPLINES = ["helicopter", "aeroplane", "drone", "other"];
const SOURCE_KEYS = [{ v: "", label: "Manual entry" }, { v: "total_hours", label: "Auto — total logged hours" }];
const uid = () => Math.random().toString(36).slice(2, 9);
const blank = () => ({ name: "", title: "Certificate of Compliance", discipline: "helicopter", regulatory_ref: "", body_text: "", requirement_rows: [], show_hours_table: true, active: true, seq: 0 });

export default function CertificatesPage({ view = "grid" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/certificates/templates"); setTemplates(r.data || []); }
    catch { toast.error("Failed to load certificates"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete certificate template “${t.name}”?`)) return;
    try { await api.delete(`/certificates/templates/${t.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="certificates-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-heading text-slate-900 flex items-center gap-2"><Award className="h-6 w-6 text-lime-500" /> Certificates</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage certificate templates. Generate a student's certificate from their training record.</p>
        </div>
        {isAdmin && <Button onClick={() => setEditing(blank())} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="new-certificate-btn"><Plus className="h-4 w-4 mr-1.5" /> New template</Button>}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-16">No certificate templates yet.</p>
      ) : view === "list" ? (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden" data-testid="certificates-list">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`certificate-row-${t.id}`}>
              <Award className="h-5 w-5 text-lime-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 truncate">{t.title}</p>
                <p className="text-xs text-slate-500 truncate">{t.name}{t.regulatory_ref ? ` · ${t.regulatory_ref}` : ""}</p>
              </div>
              <Badge className="border bg-sky-100 text-sky-700 border-sky-200 capitalize shrink-0 hidden sm:inline-flex">{t.discipline}</Badge>
              {isAdmin && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" onClick={() => setEditing({ ...t })} data-testid={`edit-certificate-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(t)} data-testid={`delete-certificate-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="border border-slate-200 rounded-2xl bg-white p-5" data-testid={`certificate-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="font-heading font-bold text-lg text-slate-900">{t.title}</h2>
                  <p className="text-sm text-slate-500 truncate">{t.name}</p>
                </div>
                <Badge className="border bg-sky-100 text-sky-700 border-sky-200 capitalize shrink-0">{t.discipline}</Badge>
              </div>
              {t.regulatory_ref && <p className="text-xs text-slate-400 mt-1">{t.regulatory_ref}</p>}
              <p className="text-sm text-slate-600 mt-3 line-clamp-3">{(t.body_text || "").replace("{{name}}", "the student")}</p>
              {t.show_hours_table && t.requirement_rows?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.requirement_rows.map((r) => <Badge key={r.id} variant="outline" className="text-[10px] text-slate-500">{r.label}: {r.required}h</Badge>)}
                </div>
              )}
              {isAdmin && (
                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...t })} data-testid={`edit-certificate-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => remove(t)} data-testid={`delete-certificate-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && <TemplateEditor tpl={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateEditor({ tpl, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...tpl, requirement_rows: (tpl.requirement_rows || []).map((r) => ({ ...r })) }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setF((p) => ({ ...p, requirement_rows: p.requirement_rows.map((r, j) => j === i ? { ...r, [k]: v } : r) }));
  const addRow = () => setF((p) => ({ ...p, requirement_rows: [...p.requirement_rows, { id: uid(), label: "", required: "", source_key: "" }] }));
  const delRow = (i) => setF((p) => ({ ...p, requirement_rows: p.requirement_rows.filter((_, j) => j !== i) }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd);
      set("logo_path", r.data.path);
      toast.success("Logo uploaded");
    } catch { toast.error("Logo upload failed"); } finally { setUploading(false); }
  };
  const logoUrl = f.logo_path ? `${API}/files/${f.logo_path}?auth=${encodeURIComponent(localStorage.getItem("session_token") || "")}` : null;

  const save = async () => {
    if (!f.name.trim() || !f.title.trim()) { toast.error("Name and title are required"); return; }
    setSaving(true);
    const payload = { ...f, requirement_rows: f.requirement_rows.filter((r) => (r.label || "").trim()) };
    delete payload.id; delete payload.seed_key; delete payload.created_at;
    try {
      if (f.id) await api.put(`/certificates/templates/${f.id}`, payload);
      else await api.post("/certificates/templates", payload);
      toast.success("Certificate template saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="certificate-editor">
        <DialogHeader>
          <DialogTitle className="font-heading">{f.id ? "Edit certificate" : "New certificate"}</DialogTitle>
          <DialogDescription>Use <code className="text-xs bg-slate-100 px-1 rounded">{"{{name}}"}</code> in the body to insert the student's name (it also appears above the body automatically).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Template name</Label>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="e.g. PPL(H) — Compliance" data-testid="cert-name" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Title (on certificate)</Label>
              <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="cert-title" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Discipline</Label>
              <Select value={f.discipline} onValueChange={(v) => set("discipline", v)}><SelectTrigger className="mt-1.5" data-testid="cert-discipline"><SelectValue /></SelectTrigger>
                <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Regulatory reference</Label>
              <Input value={f.regulatory_ref} onChange={(e) => set("regulatory_ref", e.target.value)} className="mt-1.5" placeholder="CASR Part 61.235" data-testid="cert-ref" /></div>
          </div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Body text</Label>
            <Textarea value={f.body_text} onChange={(e) => set("body_text", e.target.value)} className="mt-1.5" rows={6} data-testid="cert-body" /></div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Logo</Label>
            <div className="mt-1.5 flex items-center gap-3 rounded-lg border border-slate-200 p-3">
              <div className="h-16 w-28 flex items-center justify-center rounded bg-slate-50 border border-slate-100 overflow-hidden shrink-0">
                {logoUrl ? <img src={logoUrl} alt="logo" className="h-full object-contain" data-testid="cert-logo-preview" />
                  : <span className="text-[10px] text-slate-400 text-center px-1">Default Heli Dynamic logo</span>}
              </div>
              <div className="flex-1">
                <input type="file" accept="image/png,image/jpeg,image/webp" id="cert-logo-input" className="hidden" data-testid="cert-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <Button type="button" size="sm" variant="outline" onClick={() => document.getElementById("cert-logo-input").click()} disabled={uploading} data-testid="cert-logo-upload">
                  {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} {f.logo_path ? "Replace logo" : "Upload logo"}
                </Button>
                {f.logo_path && <Button type="button" size="sm" variant="ghost" className="ml-2 text-slate-400" onClick={() => set("logo_path", "")} data-testid="cert-logo-remove"><ImageOff className="h-4 w-4 mr-1.5" /> Use default</Button>}
                <p className="text-[11px] text-slate-400 mt-1">PNG on white works best. Leave empty to use the bundled Heli Dynamic logo.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5">
            <Label className="text-sm text-slate-700">Show required/actual hours table</Label>
            <Switch checked={f.show_hours_table} onCheckedChange={(v) => set("show_hours_table", v)} data-testid="cert-show-hours" />
          </div>

          {f.show_hours_table && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Requirement rows</Label>
              <div className="mt-2 space-y-2">
                {f.requirement_rows.map((r, i) => (
                  <div key={r.id || i} className="flex items-center gap-2" data-testid={`cert-row-${i}`}>
                    <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                    <Input value={r.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Label (e.g. Total time)" className="h-9 text-sm flex-1" data-testid={`cert-row-label-${i}`} />
                    <Input value={r.required} onChange={(e) => setRow(i, "required", e.target.value)} placeholder="Req." className="h-9 text-sm w-20" data-testid={`cert-row-req-${i}`} />
                    <Select value={r.source_key || "manual"} onValueChange={(v) => setRow(i, "source_key", v === "manual" ? "" : v)}>
                      <SelectTrigger className="h-9 text-sm w-44 shrink-0" data-testid={`cert-row-source-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCE_KEYS.map((s) => <SelectItem key={s.v || "manual"} value={s.v || "manual"}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-500 shrink-0" onClick={() => delRow(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="text-xs h-8" onClick={addRow} data-testid="cert-add-row"><Plus className="h-3.5 w-3.5 mr-1" /> Add row</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="cert-save">{saving ? "Saving…" : "Save template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
